/** EPBCS Phase E — Excel import for masters & budget data. */
import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, UploadSimple, CircleNotch, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { Card, Btn } from "./common";

const UploadCard = ({ kind, title, subtitle, endpoint, resultKey, allowUpdate }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [updateMode, setUpdateMode] = useState(false);

  const downloadTemplate = async () => {
    try {
      const r = await api.get(`/epbcs/upload/template?kind=${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vaku_${kind}_template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download template");
    }
  };

  const downloadExport = async () => {
    try {
      const r = await api.get("/epbcs/upload/export-masters", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vaku_masters_export.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not export masters");
    }
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (allowUpdate && updateMode) fd.append("mode", "upsert");
      const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      const ok = r.data[resultKey] + (r.data.updated || 0);
      if (r.data.failed) toast.warning(`${ok} rows processed, ${r.data.failed} rejected — see details`);
      else toast.success(`${ok} rows processed successfully`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Card title={title} subtitle={subtitle} testid={`import-card-${kind}`}>
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={downloadTemplate} data-testid={`template-btn-${kind}`}
          className="inline-flex items-center gap-1.5 border border-[#0B1E3F] text-[#0B1E3F] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded hover:bg-slate-50">
          <DownloadSimple size={13} weight="bold" /> Blank template
        </button>
        {allowUpdate && (
          <button onClick={downloadExport} data-testid={`export-btn-${kind}`}
            className="inline-flex items-center gap-1.5 border border-[#C8A24B] text-[#8a6d2b] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded hover:bg-amber-50/60">
            <DownloadSimple size={13} weight="bold" /> Export current masters
          </button>
        )}
        <label className="flex-1 min-w-[220px] border border-dashed border-slate-300 px-3 py-2 text-[12px] text-slate-500 cursor-pointer hover:border-[#C8A24B] rounded-sm">
          {file ? file.name : "Choose completed .xlsx file…"}
          <input type="file" accept=".xlsx" className="hidden" data-testid={`file-input-${kind}`}
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} />
        </label>
        <Btn onClick={upload} disabled={!file || busy} data-testid={`upload-btn-${kind}`}>
          <span className="flex items-center gap-1.5">
            {busy ? <CircleNotch size={13} className="animate-spin" /> : <UploadSimple size={13} weight="bold" />}
            {busy ? "Importing…" : "Upload & validate"}
          </span>
        </Btn>
      </div>
      {allowUpdate && (
        <label className="mt-3 flex items-center gap-2 text-[12px] text-slate-600 cursor-pointer select-none w-fit"
          data-testid={`update-mode-${kind}`}>
          <input type="checkbox" checked={updateMode} onChange={(e) => setUpdateMode(e.target.checked)}
            className="accent-[#0B1E3F]" data-testid={`update-mode-checkbox-${kind}`} />
          Update existing members when codes match (round-trip edit) — otherwise duplicates are rejected
        </label>
      )}
      {result && (
        <div className="mt-4" data-testid={`import-result-${kind}`}>
          <div className="flex items-center gap-4 text-[12px]">
            <span className="flex items-center gap-1.5 text-[#1F6B4A] font-semibold">
              <CheckCircle size={14} weight="fill" /> {result[resultKey]} imported
            </span>
            {result.updated > 0 && (
              <span className="flex items-center gap-1.5 text-[#0B1E3F] font-semibold" data-testid={`updated-count-${kind}`}>
                <CheckCircle size={14} weight="fill" className="text-[#C8A24B]" /> {result.updated} updated
              </span>
            )}
            {result.failed > 0 && (
              <span className="flex items-center gap-1.5 text-red-700 font-semibold">
                <WarningCircle size={14} weight="fill" /> {result.failed} rejected
              </span>
            )}
          </div>
          {(result.errors || []).length > 0 && (
            <div className="mt-2 border border-red-200 bg-red-50/50 max-h-56 overflow-auto">
              <table className="text-[11px] w-full">
                <thead>
                  <tr className="text-left text-[9px] uppercase tracking-wider text-red-800 border-b border-red-200">
                    <th className="py-1.5 px-3 w-16">Row</th><th className="py-1.5 px-2">Why it was rejected</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.map((e, i) => (
                    <tr key={i} className="border-b border-red-100" data-testid={`import-error-${kind}-${e.row}`}>
                      <td className="py-1 px-3 font-mono">{e.row}</td>
                      <td className="py-1 px-2 text-red-800">{e.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default function DataImportTab() {
  return (
    <div className="space-y-4 max-w-4xl" data-testid="data-import-tab">
      <UploadCard kind="masters" title="Import Dimension Masters" allowUpdate
        subtitle="One sheet per dimension (Entities, Divisions, Lines of Business, Departments, Accounts…) showing exactly the fields each dimension needs — same validation as manual entry: duplicate codes rejected, mandatory fields enforced. Export current masters to edit offline and re-upload with update mode."
        endpoint="/epbcs/upload/masters" resultKey="created" />
      <UploadCard kind="budget" title="Import Budget Data"
        subtitle="Monthly budget values per form, POV and account. Unknown members are rejected, actual months are protected, and values must be positive."
        endpoint="/epbcs/upload/budget" resultKey="saved" />
      <UploadCard kind="capex_wbs" title="Import CapEx Projects & Tasks"
        subtitle="One row per task. Repeat the Project Name/Code across rows to add several tasks to the same project — leave Project Code blank on a new project's first row to auto-generate its code."
        endpoint="/epbcs/upload/capex-wbs" resultKey="created" />
    </div>
  );
}
